import React, { useMemo } from "react";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  RefreshCw,
  Download,
} from "lucide-react";
import { resolveKortexDocumentLink } from "../../utils/kortexDocumentLink";

function formatDocumentDate(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function sortDocuments(documents) {
  return [...documents].sort((a, b) => {
    const dateA = a.document_date ? new Date(a.document_date).getTime() : 0;
    const dateB = b.document_date ? new Date(b.document_date).getTime() : 0;
    return dateA - dateB;
  });
}

function documentInstanceLabel(document) {
  const raw =
    document?.instance_name || document?.instanceName || document?.instance;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return `${raw}º grau`;
  }
  if (typeof raw !== "string") return "";
  return raw.trim();
}

function instanceSortRank(instanceName) {
  const label = String(instanceName || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (/\b1\b|primeiro/.test(label)) return 0;
  if (/\b2\b|segundo/.test(label)) return 1;
  if (/superior|stj|stf|tst/.test(label)) return 2;
  return 3;
}

function groupDocumentsByInstance(documents) {
  if (!documents.some((document) => documentInstanceLabel(document))) {
    return null;
  }

  const groups = new Map();
  for (const document of documents) {
    const key = documentInstanceLabel(document) || "Instância não informada";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(document);
  }

  return Array.from(groups.entries())
    .map(([instanceName, items]) => ({ instanceName, items }))
    .sort(
      (a, b) => instanceSortRank(a.instanceName) - instanceSortRank(b.instanceName),
    );
}

function DocumentList({ documents }) {
  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
      {documents.map((document) => {
        const documentLink = resolveKortexDocumentLink(document);

        return (
          <li
            key={document.uid || `${document.name}-${document.document_date}`}
            className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
          >
            <FileText
              className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5"
              aria-hidden
            />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {document.name || "Documento sem nome"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {formatDocumentDate(document.document_date)}
              </p>
            </div>

            {documentLink ? (
              <a
                href={documentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 flex-shrink-0 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Baixar
                <Download className="w-3.5 h-3.5" aria-hidden />
              </a>
            ) : (
              <span className="text-xs text-gray-400 flex-shrink-0">
                Sem link
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function ProcessDocumentsPanel({
  documentsResponse,
  loading,
  error,
  isExpanded,
  onToggle,
}) {
  const documents = documentsResponse?.documents || [];

  const sortedDocuments = useMemo(() => sortDocuments(documents), [documents]);

  const renderBody = () => {
    if (loading && !documents.length) {
      return (
        <div className="flex items-center justify-center py-10 gap-2 text-gray-600">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-sm">Carregando documentos...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Não foi possível carregar os documentos
            </p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      );
    }

    if (!sortedDocuments.length) {
      return (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertCircle
            className="w-5 h-5 text-amber-600 flex-shrink-0"
            aria-hidden
          />
          <p className="text-sm font-semibold text-amber-800">
            Não foram encontrados documentos para este processo.
          </p>
        </div>
      );
    }

    const groupedDocuments = groupDocumentsByInstance(sortedDocuments);

    return (
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          {sortedDocuments.length} documento
          {sortedDocuments.length === 1 ? "" : "s"} disponíve
          {sortedDocuments.length === 1 ? "l" : "is"}
        </p>

        {groupedDocuments ? (
          <div className="space-y-4">
            {groupedDocuments.map(({ instanceName, items }) => (
              <section key={instanceName} className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  Instância: {instanceName}
                </h4>
                <DocumentList documents={items} />
              </section>
            ))}
          </div>
        ) : (
          <DocumentList documents={sortedDocuments} />
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              Documentos do processo
            </h3>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {documents.length
                  ? `${documents.length} documento${documents.length === 1 ? "" : "s"} — clique para expandir`
                  : "Documentos processuais — clique para expandir"}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp
            className="w-5 h-5 text-gray-600 flex-shrink-0"
            aria-hidden
          />
        ) : (
          <ChevronDown
            className="w-5 h-5 text-gray-600 flex-shrink-0"
            aria-hidden
          />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <div className="pt-4">{renderBody()}</div>
        </div>
      )}
    </div>
  );
}
